import classNames from 'classnames';
import { useCreateGenres, useCreateLike, useTrending } from '@bpm-web-app/swr-hooks';
import { useState, Fragment, useMemo } from 'react';
import {
    appendQueryParams,
    DEFAULT_DROPDOWN_CATEGORY,
    createAppRoutes,
    soundPackageToCreatePlayable,
    useViewport,
    convertSoundPackageToSeeMoreLayoutProps,
    useCreateFilterParams,
    CreateCardCarouselItem,
} from '@bpm-web-app/utils';
import { SortByQueryOptions, SoundPackage } from '@bpm-web-app/create-api-sdk';
import { useRouter } from 'next/router';
import styles from './trending-packs.module.css';
import Dropdown from '../../dropdown/dropdown';
import CardGrid from '../../shared/card-grid/card-grid';
import CardCreate from '../../shared/card/card-create';
import UpArrow from '../../../assets/icons/create/up-arrow.svg';
import DownArrow from '../../../assets/icons/create/down-arrow.svg';
import { useCreatePlayer } from '../../../../../utils/src/lib/create-player.context';
import { ClickableGenres } from '../../clickable-genres/clickable-genres';
import Filters from '../../filters/filters';
import { PageNotFound } from '../page-not-found/page-not-found';
import { CardCarouselGhostLoading } from '../../shared/card-carousel/card-carousel-ghost-loading';
import { EmptyState } from '../../shared';

export function TrendingPacks() {
    const router = useRouter();

    const [activeGenre, setActiveGenre] = useState<string>(DEFAULT_DROPDOWN_CATEGORY.value);
    const { genres: genresData } = useCreateGenres();

    const { isLiked, likeDislike } = useCreateLike('soundPackage');
    const { isMobile } = useViewport();
    const { playDemo } = useCreatePlayer();

    const { sortBy } = router.query;
    const overrides = useMemo(() => {
        if (sortBy) {
            return { sort_by: `${sortBy}` as unknown as SortByQueryOptions, limit: 50 };
        }
        return { limit: 50 };
    }, [sortBy]);
    const query = useCreateFilterParams(overrides);

    const { data: trending, isLoading, error } = useTrending('create', { ...query, genre: activeGenre });

    const trendingPacksItems = ((trending?.data as SoundPackage[]) || []).map((item) => ({
        ...convertSoundPackageToSeeMoreLayoutProps(item, <ClickableGenres genres={item.Genre} />),
        trending_status: (item as any).trending_status,
    }));

    const getTrendingStatus = (trendingStatus: number, index: number) => {
        if (trendingStatus > 0) {
            return (
                <div className={styles['trending-pack__status-container']}>
                    <span className={classNames(styles['trending-pack__status-text'], styles['trending-pack__status-text--up'])}>{index > 9 ? index : `0${index}`}</span>
                    <UpArrow />
                </div>
            );
        }

        return (
            <div className={styles['trending-pack__status-container']}>
                <span className={classNames(styles['trending-pack__status-text'], styles['trending-pack__status-text--down'])}>{index > 9 ? index : `0${index}`}</span>
                <DownArrow />
            </div>
        );
    };

    if (!isLoading && (error || !trending)) return <PageNotFound />;

    return (
        <div className={classNames(styles['trending-pack'], 'spacing__window')}>
            <div className={styles['trending-pack__intro']}>
                <h1 className={styles['trending-pack__title']}>Trending</h1>
                <Filters hideCreateGenres showOnMobile platform="create" />
            </div>
            <div className={styles['trending-pack__sub-intro']}>
                <h2 className={styles['trending-pack__subtitle']}>Packs</h2>
                <div className={styles['trending-pack__period-dropdown']}>
                    {genresData?.length && (
                        <Dropdown<string>
                            onClick={setActiveGenre}
                            value={activeGenre}
                            options={[
                                DEFAULT_DROPDOWN_CATEGORY,
                                ...(genresData?.map((genre) => ({
                                    label: genre.name,
                                    value: genre.name,
                                })) || []),
                            ]}
                        />
                    )}
                </div>
            </div>

            {trendingPacksItems.length === 0 && !isLoading ? (
                <EmptyState title={`No ${activeGenre} Packs Found`} subtitle="" noPadding icon="search-outline-icon" variant="white" />
            ) : null}

            {!isLoading ? (
                <div className={styles['trending-pack__grid-container']}>
                    <CardGrid cardType={isMobile ? 'card-label' : 'card-small'}>
                        {trendingPacksItems.map((item, index) => (
                            <Fragment key={item.id}>
                                <CardCreate
                                    hasExtraDemo={!!item.demo_file_url_2}
                                    contentType="pack"
                                    key={item.id}
                                    id={`${item.id}`}
                                    title={item.title}
                                    description={item.description}
                                    tag={getTrendingStatus((item as any).trending_status, index + 1)}
                                    imageUrl={appendQueryParams(item.image_url, { key: 'dw', value: 163 })}
                                    imageUrl2x={appendQueryParams(item.image_url, { key: 'dw', value: 326 })}
                                    cardSize={isMobile ? 'user-playlist' : 'small'}
                                    isFavorite={isLiked(String(item.id))}
                                    onFavorite={likeDislike}
                                    onCardPlay={item.isPlayable ? (id) => playDemo(soundPackageToCreatePlayable((trending?.data as SoundPackage[]).find((cardItem) => cardItem.id === id))) : undefined}
                                    link={createAppRoutes.packsSlug(item.slug)}
                                    items={trendingPacksItems as unknown as CreateCardCarouselItem[]}
                                />
                            </Fragment>
                        ))}
                    </CardGrid>
                </div>
            ) : (
                <CardCarouselGhostLoading elementsCount={10} cardSize={isMobile ? 'label' : 'small'} grid />
            )}
        </div>
    );
}

export default TrendingPacks;
